import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  BadgeCheck,
  ArrowRight,
  RefreshCcw,
  Loader2,
  Copy,
  Check,
  ExternalLink,
  ArrowUpRight,
} from "lucide-react";

import { api, formatCurrency } from "@/lib/api";

// ---- status config ----
const STATUS_META = {
  verified: {
    label: "Verified",
    icon: ShieldCheck,
    dot: "bg-[#0055FF]",
    iconText: "text-[#0055FF]",
    badge: "bg-blue-50 text-[#0055FF] border border-blue-200",
    section: {
      title: "Verified Certificates",
      subtitle: "All verification checks pass against issuer-backed records.",
    },
  },
  unverified_ownership: {
    label: "Ownership Not Confirmed",
    icon: ShieldAlert,
    dot: "bg-amber-500",
    iconText: "text-amber-600",
    badge: "bg-amber-50 text-amber-700 border border-amber-200",
    section: {
      title: "Ownership Not Verified",
      subtitle: "Record exists, but ownership has not been transferred.",
    },
  },
  mismatch: {
    label: "Mismatch",
    icon: ShieldAlert,
    dot: "bg-amber-500",
    iconText: "text-amber-600",
    badge: "bg-amber-50 text-amber-700 border border-amber-200",
    section: {
      title: "Mismatch Detected",
      subtitle: "One or more fields do not match the issuer record.",
    },
  },
  not_found: {
    label: "Not Verified",
    icon: ShieldX,
    dot: "bg-red-500",
    iconText: "text-red-500",
    badge: "bg-red-50 text-red-700 border border-red-200",
    section: {
      title: "Not Verified",
      subtitle: "No matching record found in the system.",
    },
  },
};

const ORDER = ["verified", "unverified_ownership", "mismatch", "not_found"];

// ---- card ----
const ScenarioCard = ({ entry, onTransfer, transferring }) => {
  const meta = STATUS_META[entry.status] || STATUS_META.verified;
  const Icon = meta.icon;
  const [copied, setCopied] = useState(false);
  const canTransfer = entry.status === "unverified_ownership" && entry.receipt_id;

  const verifyUrl = `/verify/${entry.nft_id}`;
  const fullUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${verifyUrl}`
      : verifyUrl;

  const copyId = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(entry.nft_id);
      setCopied(true);
      toast.success("NFT ID copied");
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div
      data-testid={`scenario-card-${entry.nft_id}`}
      className="group bg-white border border-gray-200 rounded-xl shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_16px_36px_rgba(0,0,0,0.1)] hover:border-gray-300 hover:-translate-y-1 transition-all duration-300 ease-out overflow-hidden flex flex-col"
    >
      <div className="p-5 flex items-start gap-4">
        <div className="shrink-0 h-11 w-11 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center group-hover:scale-105 transition-transform">
          <Icon size={20} strokeWidth={2.2} className={meta.iconText} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500 truncate">
            {entry.brand || "Unknown Retailer"}
          </div>
          <div className="mt-1 font-display font-bold text-gray-900 text-base leading-snug truncate">
            {entry.item || entry.label}
          </div>
          {entry.total != null && entry.currency && (
            <div className="mt-1 font-mono text-xs text-gray-500">
              {formatCurrency(entry.total, entry.currency)}
            </div>
          )}
        </div>
      </div>

      <div className="px-5 pb-3 flex items-center justify-between gap-2">
        <span
          className={`inline-flex items-center gap-1.5 ${meta.badge} px-2.5 py-0.5 rounded-full text-[11px] font-semibold`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
          {meta.label}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={copyId}
            data-testid={`copy-nft-${entry.nft_id}`}
            title="Copy NFT ID"
            className="p-1.5 rounded-md text-gray-400 hover:text-[#0055FF] hover:bg-blue-50 transition"
          >
            {copied ? <Check size={13} className="text-green-600" /> : <Copy size={13} />}
          </button>
          <a
            href={verifyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`open-new-tab-${entry.nft_id}`}
            title="Open in new tab"
            className="p-1.5 rounded-md text-gray-400 hover:text-[#0055FF] hover:bg-blue-50 transition"
            onClick={(e) => e.stopPropagation()}
          >
            <ArrowUpRight size={13} />
          </a>
        </div>
      </div>

      <div className="px-5 pb-3 font-mono text-[10px] text-gray-400 truncate">
        {entry.nft_id}
      </div>

      <div className="mt-auto px-5 py-4 border-t border-gray-100 bg-gray-50/40 flex flex-col gap-2">
        <Link
          to={verifyUrl}
          state={{ celebrate: entry.status === "verified" }}
          data-testid={`view-cert-link-${entry.nft_id}`}
          className="w-full inline-flex items-center justify-center gap-1.5 bg-white border border-gray-200 text-gray-900 text-xs font-semibold px-3 py-2 rounded-lg hover:border-[#0055FF] hover:text-[#0055FF] transition"
        >
          <BadgeCheck size={13} />
          View Certificate
          <ArrowRight size={11} />
        </Link>
        {canTransfer && (
          <button
            onClick={() => onTransfer(entry)}
            disabled={transferring}
            data-testid={`simulate-transfer-${entry.nft_id}`}
            className="w-full inline-flex items-center justify-center gap-1.5 bg-[#0055FF] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#0044CC] shadow-sm hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-wait"
          >
            {transferring ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <ArrowRight size={13} />
            )}
            {transferring ? "Transferring…" : "Simulate Transfer"}
          </button>
        )}
      </div>
    </div>
  );
};

// ---- section ----
const Section = ({ status, entries, onTransfer, transferringId }) => {
  const meta = STATUS_META[status];
  if (!entries || entries.length === 0) return null;
  return (
    <section data-testid={`section-${status}`} className="mb-10">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="font-display font-bold text-xl text-gray-900">
            {meta.section.title}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">{meta.section.subtitle}</p>
        </div>
        <span className="text-xs font-mono text-gray-400">
          {entries.length} scenario{entries.length > 1 ? "s" : ""}
        </span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {entries.map((e) => (
          <ScenarioCard
            key={e.nft_id}
            entry={e}
            onTransfer={onTransfer}
            transferring={transferringId === e.receipt_id}
          />
        ))}
      </div>
    </section>
  );
};

// ---- page ----
export default function Verification() {
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [transferringId, setTransferringId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: minted } = await api.get("/receipts/minted");

      const results = await Promise.all(
        (minted || []).map(async (r) => {
          try {
            const { data } = await api.get(`/verify/${r.nft_id}`);
            return {
              status: data.status,
              nft_id: r.nft_id,
              receipt_id: r.id,
              brand: data.brand || r.store_name,
              item: r.items?.[0]?.name,
              total: r.total,
              currency: r.currency,
            };
          } catch {
            return null;
          }
        })
      );

      const bucket = {
        verified: [],
        unverified_ownership: [],
        mismatch: [],
        not_found: [],
      };

      results.forEach((r) => {
        if (r && bucket[r.status]) bucket[r.status].push(r);
      });

      bucket.not_found.push({
        status: "not_found",
        nft_id: "NFT-FAKE12345",
        brand: "Unknown Issuer",
        item: "Unrecognized NFT Record",
        label: "Fake NFT",
      });

      setGroups(bucket);
    } catch {
      toast.error("Failed to load verification scenarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleReset = async () => {
    setResetting(true);
    try {
      await api.post("/admin/reset");
      toast.success("Demo data reset");
      await load();
    } catch {
      toast.error("Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const handleTransfer = async (entry) => {
    if (!entry.receipt_id) return;
    setTransferringId(entry.receipt_id);
    try {
      await api.post(`/receipts/${entry.receipt_id}/simulate-transfer`);
      // Optimistic UI move: pull from unverified_ownership → push to verified
      setGroups((prev) => {
        if (!prev) return prev;
        const next = {
          verified: [...prev.verified],
          unverified_ownership: prev.unverified_ownership.filter(
            (e) => e.nft_id !== entry.nft_id
          ),
          mismatch: [...prev.mismatch],
          not_found: [...prev.not_found],
        };
        next.verified.unshift({ ...entry, status: "verified" });
        return next;
      });
      toast.success("Ownership successfully transferred");
    } catch {
      toast.error("Transfer failed");
    } finally {
      setTransferringId(null);
    }
  };

  return (
    <div
      data-testid="verification-page"
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
        <div>
          <div className="text-xs font-semibold tracking-wider uppercase text-[#0055FF]">
            Verification
          </div>
          <h1 className="font-display font-bold tracking-tight text-gray-900 text-3xl sm:text-4xl mt-1">
            Verification Scenarios
          </h1>
          <p className="mt-3 text-gray-600 max-w-xl">
            Test and view different verification outcomes for digital asset
            records.
          </p>
        </div>
        <button
          onClick={handleReset}
          disabled={resetting}
          data-testid="reset-demo-data-btn"
          className="inline-flex items-center gap-2 bg-white text-gray-900 border border-gray-200 text-sm font-semibold px-4 py-2 rounded-lg hover:border-[#0055FF] hover:text-[#0055FF] transition disabled:opacity-60"
        >
          {resetting ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <RefreshCcw size={14} />
          )}
          {resetting ? "Resetting…" : "Reset Demo Data"}
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 mb-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {ORDER.map((s) => {
          const meta = STATUS_META[s];
          const count =
            s === "not_found" ? 1 : (groups?.[s] || []).length;
          return (
            <div key={s} className="flex items-center gap-2.5">
              <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
              <div className="min-w-0">
                <div className="text-[11px] font-semibold tracking-wider uppercase text-gray-500 truncate">
                  {meta.label}
                </div>
                <div className="text-xs text-gray-400 font-mono">
                  {loading ? "—" : `${count} scenario${count === 1 ? "" : "s"}`}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {loading ? (
        <div className="py-24 text-center text-gray-500">Loading scenarios…</div>
      ) : (
        <>
          {ORDER.map((s) => (
            <Section
              key={s}
              status={s}
              entries={groups?.[s] || []}
              onTransfer={handleTransfer}
              transferringId={transferringId}
            />
          ))}
        </>
      )}
    </div>
  );
}
